import {Component, inject, OnInit} from '@angular/core';
import {DialogBaseComponent} from "../../dialogs/dialog-base/dialog-base.component";
import {
  ButtonCloseDirective,
  ButtonDirective,
  FormCheckComponent,
  FormCheckInputDirective,
  FormCheckLabelDirective,
  FormControlDirective,
  FormDirective,
  FormFeedbackComponent,
  FormLabelDirective,
  ModalModule
} from "@coreui/angular";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatButton} from "@angular/material/button";
import {SnackbarService} from "../../../services/snackbar.service";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {IApiErrorResponse} from "../../../interfaces/error/IApiErrorResponse";
import {FrequentlyAskedQuestionsService} from "../../../services/frequently-asked-questions.service";
import {IFrequentlyAskedQuestions} from "../../../interfaces/IFrequentlyAskedQuestions";
import {MarkdownComponent} from "ngx-markdown";
import {GameService} from "../../../services/game.service";
import {FaqScopeTypes} from "../../../enum/FaqScopeTypes";
import {PermissionsService} from "../../../services/permissions.service";
import {PermissionTypes} from "../../../enum/PermissionTypes";

@Component({
  selector: 'app-faqs-edit',
  standalone: true,
  imports: [
    DialogBaseComponent,
    FormCheckComponent,
    FormCheckInputDirective,
    FormCheckLabelDirective,
    FormControlDirective,
    FormDirective,
    FormFeedbackComponent,
    FormLabelDirective,
    FormsModule,
    MatButton,
    ReactiveFormsModule,
    ButtonDirective,
    ModalModule,
    ButtonCloseDirective,
    MarkdownComponent
  ],
  templateUrl: './faqs-edit.component.html',
  styleUrl: './faqs-edit.component.css'
})
export class FaqsEditComponent implements OnInit {
  private frequentlyAskedQuestionsService = inject(FrequentlyAskedQuestionsService);
  private snackbarService = inject(SnackbarService);
  private gameService = inject(GameService);
  private permissionsService = inject(PermissionsService);
  private matDialogRef = inject(MatDialogRef<FaqsEditComponent>);
  private dialogData = inject<{ faq: IFrequentlyAskedQuestions }>(MAT_DIALOG_DATA);
  public faq = this.dialogData.faq;

  uiState: UIStateEnum = UIStateEnum.ShowData;
  uiStateEnumForTemplate = UIStateEnum;
  answerContentControl = new FormControl<string | null>("", Validators.required);
  questionControl = new FormControl<string | null>("", Validators.required);
  activeControl = new FormControl<boolean>(false);
  scopeTypeControl = new FormControl<number>(1, Validators.required);

  editFaqForm = new FormGroup({
    question: this.questionControl,
    answer: this.answerContentControl,
    active: this.activeControl,
    scopeType: this.scopeTypeControl
  });

  public canEditWebappFaqs: boolean = false;
  public canEditAdminAppFaqs: boolean = false;
  public availableScopes: Array<{value: number, label: string}> = [];

  ngOnInit() {
    this.canEditWebappFaqs = this.permissionsService.userHasPermission(PermissionTypes.ModifyWebappFrequentlyAskedQuestions);
    this.canEditAdminAppFaqs = this.permissionsService.userHasPermission(PermissionTypes.ModifyAdminAppFrequentlyAskedQuestions);

    // Build available scope options based on permissions
    if (this.canEditWebappFaqs) {
      this.availableScopes.push({ value: FaqScopeTypes.WebApp, label: 'Web App' });
    }
    if (this.canEditAdminAppFaqs) {
      this.availableScopes.push({ value: FaqScopeTypes.AdminApp, label: 'Admin App' });
    }

    this.populateForm(this.faq);
  }

  populateForm(faqP: IFrequentlyAskedQuestions) {
    this.editFaqForm.setValue({
      question: faqP.Question,
      answer: faqP.Answer,
      active: faqP.Active,
      scopeType: faqP.ScopeType
    })
  }

  editFaq() {
    this.editFaqForm.markAllAsTouched();

    if (!this.editFaqForm.valid) {
      return;
    }

    this.uiState = UIStateEnum.ShowLoading;

    // Only include gameId if there's an active game (game admin context)
    const activeGame = this.gameService.activeGame();

    const faqData = {
      active: this.activeControl.value!,
      id: this.faq.Id,
      answer: this.answerContentControl.value!,
      question: this.questionControl.value!,
      ScopeType: this.scopeTypeControl.value!,
      shared: this.faq.Shared,
      // Preserve the FAQ's existing game association. Sending [] here orphaned
      // game-specific FAQs on edit, making them vanish from the list (issue #1345).
      gameIds: this.faq.GameIds ?? [],
      gameId: activeGame?.Id
    };

    // The endpoint decides the app, not the payload - each one overwrites ScopeType with its own.
    // Routing on the FAQ's existing scope therefore sent every edit back to the app it was already
    // in, so changing the app reported success and changed nothing (#1526).
    const serviceCall = this.scopeTypeControl.value === FaqScopeTypes.WebApp
      ? this.frequentlyAskedQuestionsService.editWebappFaq(faqData)
      : this.frequentlyAskedQuestionsService.editAdminAppFaq(faqData);

    serviceCall.subscribe({
      next: () => {
        this.uiState = UIStateEnum.ShowData;
        this.snackbarService.openSuccessfulSnackBar('Successfully edited FAQ');
        this.matDialogRef.close();
      }, error: (res: IApiErrorResponse) => {
        this.uiState = UIStateEnum.ShowData;
        if (res.Error?.ClientErrorMessages?.length > 0) {
          this.snackbarService.openErrorSnackBar(res.Error.ClientErrorMessages.toString());
        } else {
          this.snackbarService.openErrorSnackBar('There was an issue editing your FAQ');
        }
      }
    })
  }
}
